<template>
  <main class="promo-main remove-main">
    <header class="promo-toolbar">
      <div class="toolbar-left">
        <span class="title">处理预览</span>
      </div>
      <div class="toolbar-right">
        <button v-if="hasAnyContent" class="ghost-btn small danger" @click="clearAllSlots">删除全部</button>
      </div>
    </header>

    <section class="remove-main-body">
      <div class="remove-preview-row">
        <div class="remove-row-head">
          <div class="remove-row-title">导入图片</div>
          <button class="ghost-btn small" @click="onBatchUploadRequest($event)">批量导入</button>
        </div>

        <input
          ref="singleInput"
          type="file"
          accept="image/*"
          class="file-input-hidden"
          @change="onSingleFileChange"
          @cancel="onPickerCancel"
        />
        <input
          ref="batchInput"
          type="file"
          accept="image/*"
          multiple
          class="file-input-hidden"
          @change="onBatchFileChange"
          @cancel="onPickerCancel"
        />

        <div class="remove-import-grid">
          <div
            v-for="(slot, index) in slotsForCurrentMode"
            :key="`${currentMode}-${slot.id}`"
            class="gallery-card remove-preview-card import-card"
            :class="{
              result: !!(slot.preview || slot.sourceUrl || slot.compressedImage),
              'is-empty': !(slot.preview || slot.sourceUrl || slot.compressedImage),
              active: currentDragFlags[index],
              'is-active-edit': index === activeSlotIndex
            }"
            @click="onImportCardClick(index, $event)"
            @dragenter.prevent="onImportDragEnter(index)"
            @dragover.prevent="onImportDragEnter(index)"
            @dragleave.prevent="onImportDragLeave(index)"
            @drop.prevent="onImportDrop($event, index)"
          >
            <template v-if="slot.preview || slot.sourceUrl || slot.compressedImage">
              <img :src="slot.preview || slot.sourceUrl || slot.compressedImage" class="card-img" :alt="`import-${index + 1}`" />
              <div class="overlay"></div>
              <button
                class="action-btn glass import-preview-btn"
                title="预览"
                @click.stop="workbench.openLightbox(slot.sourceUrl || slot.preview || slot.compressedImage)"
              >
                <svg class="icon-sm" viewBox="0 0 24 24" fill="none" stroke="currentColor">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 12a3 3 0 11-6 0 3 3 0 016 0" />
                  <path
                    stroke-linecap="round"
                    stroke-linejoin="round"
                    stroke-width="2"
                    d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z"
                  />
                </svg>
              </button>
              <div class="card-actions">
                <button
                  class="action-btn blue"
                  title="下载导入图"
                  @click.stop="downloadImage(slot.sourceUrl || slot.preview || slot.compressedImage, `import-${index + 1}.png`)"
                >
                  <svg class="icon-sm" viewBox="0 0 24 24" fill="none" stroke="currentColor">
                    <path
                      stroke-linecap="round"
                      stroke-linejoin="round"
                      stroke-width="2"
                      d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4"
                    />
                  </svg>
                </button>
                <button class="action-btn glass" title="重新上传" @click.stop="onSlotUploadRequest(index, $event)">
                  <svg class="icon-sm" viewBox="0 0 24 24" fill="none" stroke="currentColor">
                    <path
                      stroke-linecap="round"
                      stroke-linejoin="round"
                      stroke-width="2"
                      d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"
                    />
                  </svg>
                </button>
                <button class="action-btn red" title="删除" @click.stop="clearSingleImport(index)">
                  <svg class="icon-sm" viewBox="0 0 24 24" fill="none" stroke="currentColor">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                  </svg>
                </button>
              </div>
              <div class="card-label">导入 {{ index + 1 }}</div>
            </template>

            <template v-else>
              <div class="empty-card">
                <div class="icon-box">
                  <svg class="icon-svg" viewBox="0 0 24 24" fill="none" stroke="currentColor">
                    <path
                      stroke-linecap="round"
                      stroke-linejoin="round"
                      stroke-width="2"
                      d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12"
                    />
                  </svg>
                </div>
                <span class="label-main">导入图片 {{ index + 1 }}</span>
                <span class="label-sub">点击或拖拽上传</span>
              </div>
            </template>
          </div>
        </div>
      </div>

      <div class="remove-preview-row">
        <div class="remove-row-head">
          <div class="remove-row-title">抠图结果</div>
        </div>

        <div class="remove-result-grid">
          <div
            v-for="(card, index) in resultSlots"
            :key="`${currentMode}-result-${card.id}`"
            class="gallery-card remove-preview-card result-slot"
            :class="{
              loading: card.isLoading,
              result: !!card.result && !card.isLoading,
              selected: card.isSelected,
              'is-empty': !card.result && !card.isLoading
            }"
            :style="resultStageStyle"
          >
            <template v-if="card.isLoading">
              <div class="pulse-bg"></div>
              <div class="card-center">
                <div class="ring">
                  <svg class="ring-svg" viewBox="0 0 100 100">
                    <circle class="ring-track" cx="50" cy="50" r="40"></circle>
                    <circle
                      class="ring-stroke"
                      cx="50"
                      cy="50"
                      r="40"
                      :style="{ strokeDashoffset: 251.2 * (1 - removeProgress / 100) }"
                    ></circle>
                  </svg>
                  <div class="ring-text">{{ Math.round(removeProgress) }}%</div>
                </div>
                <p class="load-text">生成中...</p>
              </div>
            </template>

            <template v-else-if="card.result">
              <img :src="card.result" class="card-img" :alt="`result-${index + 1}`" />
              <div class="overlay"></div>
              <button
                class="select-btn"
                :class="{ active: card.isSelected }"
                title="设为全局参考图"
                @click.stop="onSelectResultCard(index)"
              >
                <span class="checkmark">✓</span>
              </button>
              <div class="card-actions">
                <button class="action-btn glass" title="预览" @click.stop="workbench.openLightbox(card.result)">
                  <svg class="icon-sm" viewBox="0 0 24 24" fill="none" stroke="currentColor">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 12a3 3 0 11-6 0 3 3 0 016 0" />
                    <path
                      stroke-linecap="round"
                      stroke-linejoin="round"
                      stroke-width="2"
                      d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z"
                    />
                  </svg>
                </button>
                <button
                  class="action-btn blue"
                  title="下载结果"
                  @click.stop="downloadImage(card.result, `cutout-${index + 1}.png`)"
                >
                  <svg class="icon-sm" viewBox="0 0 24 24" fill="none" stroke="currentColor">
                    <path
                      stroke-linecap="round"
                      stroke-linejoin="round"
                      stroke-width="2"
                      d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4"
                    />
                  </svg>
                </button>
                <button class="action-btn red" title="删除结果" @click.stop="clearSingleResult(index)">
                  <svg class="icon-sm" viewBox="0 0 24 24" fill="none" stroke="currentColor">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                  </svg>
                </button>
              </div>
              <div class="card-label">结果 {{ index + 1 }}</div>
            </template>

            <template v-else>
              <div class="empty-card">
                <div class="icon-box">
                  <svg class="icon-svg" viewBox="0 0 24 24" fill="none" stroke="currentColor">
                    <rect x="3" y="3" width="18" height="18" rx="2" ry="2" stroke-width="2"></rect>
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M7 13l3 3 7-7"></path>
                  </svg>
                </div>
                <span class="label-main">等待结果 {{ index + 1 }}</span>
                <span class="label-sub">上传并开始处理后显示</span>
              </div>
            </template>
          </div>
        </div>
      </div>
    </section>
  </main>
</template>

<script setup>
import { computed, nextTick, onBeforeUnmount, reactive, ref, watch } from 'vue'
import { useWorkbenchStore } from '@/stores/useWorkbenchStore'

const workbench = useWorkbenchStore()

const SLOT_COUNT = 3
const PREVIEW_MAX_EDGE = 1440

const createSlot = (index) => ({
  id: `${index}`,
  file: null,
  sourceUrl: '',
  preview: '',
  compressedImage: '',
  result: '',
  resultUpdatedAt: 0,
  loading: false,
  error: '',
  status: ''
})

const createSlots = () => Array.from({ length: SLOT_COUNT }, (_, index) => createSlot(index))
const createDragFlags = () => Array.from({ length: SLOT_COUNT }, () => false)

const slotsByMode = reactive({
  local: createSlots(),
  doubao: createSlots()
})

const dragByMode = reactive({
  local: createDragFlags(),
  doubao: createDragFlags()
})

const activeSlotByMode = reactive({
  local: 0,
  doubao: 0
})

const selectedResultByMode = reactive({
  local: -1,
  doubao: -1
})

const singleInput = ref(null)
const batchInput = ref(null)
const isPicking = ref(false)
const pendingSlotIndex = ref(-1)
const removeProgress = ref(0)
const removeProgressTimer = ref(null)
const wasActiveLoading = ref(false)
const allowEmptySlotSync = ref(false)

const REMOVE_PROGRESS_DURATION = 20000
const REMOVE_PROGRESS_INTERVAL = 200

const currentMode = computed(() => workbench.cutTab)

const slotsForCurrentMode = computed(() => slotsByMode[currentMode.value])

const currentDragFlags = computed(() => dragByMode[currentMode.value])

const activeSlotIndex = computed(() => activeSlotByMode[currentMode.value])

const resultStageStyle = computed(() => {
  const style = { ...workbench.activeStageStyle }
  delete style.minHeight
  return style
})

const resultSlots = computed(() =>
  slotsForCurrentMode.value.map((slot, index) => ({
    id: slot.id,
    result: slot.result,
    isLoading: slot.loading || (workbench.activeLoading && index === activeSlotIndex.value),
    isSelected: selectedResultByMode[currentMode.value] === index
  }))
)

const hasAnyContent = computed(() =>
  slotsForCurrentMode.value.some((slot) =>
    slot.preview || slot.sourceUrl || slot.compressedImage || slot.result
  )
)

const stopRemoveProgress = (finalValue = 0) => {
  if (removeProgressTimer.value) {
    clearInterval(removeProgressTimer.value)
    removeProgressTimer.value = null
  }
  removeProgress.value = finalValue
}

const startRemoveProgress = (durationMs = REMOVE_PROGRESS_DURATION) => {
  stopRemoveProgress(0)
  const startTimestamp = performance.now()
  removeProgressTimer.value = setInterval(() => {
    const elapsed = performance.now() - startTimestamp
    removeProgress.value = Math.min(99, (elapsed / durationMs) * 99)

    if (elapsed >= durationMs && removeProgressTimer.value) {
      clearInterval(removeProgressTimer.value)
      removeProgressTimer.value = null
    }
  }, REMOVE_PROGRESS_INTERVAL)
}

const isImageFile = (file) => !!file && typeof file.type === 'string' && file.type.startsWith('image/')

const clampSlotIndex = (value) => Math.min(Math.max(Number.isInteger(value) ? value : 0, 0), SLOT_COUNT - 1)

const revokeObjectUrl = (url) => {
  if (typeof url === 'string' && url.startsWith('blob:')) {
    URL.revokeObjectURL(url)
  }
}

const revokeSlotUrls = (slot) => {
  if (!slot) {
    return
  }
  if (slot.preview && slot.preview !== slot.sourceUrl) {
    revokeObjectUrl(slot.preview)
  }
  revokeObjectUrl(slot.sourceUrl)
  slot.preview = ''
  slot.sourceUrl = ''
}

const clearInputValue = (input) => {
  if (input && typeof input.value === 'string') {
    input.value = ''
  }
}

const getPreviewSize = (width, height) => {
  if (!width || !height) {
    return { width: 1, height: 1 }
  }
  const scale = Math.min(1, PREVIEW_MAX_EDGE / Math.max(width, height))
  return {
    width: Math.max(1, Math.round(width * scale)),
    height: Math.max(1, Math.round(height * scale))
  }
}

const canvasToBlob = (canvas) =>
  new Promise((resolve, reject) => {
    canvas.toBlob((blob) => {
      if (blob) {
        resolve(blob)
      } else {
        reject(new Error('预览生成失败'))
      }
    }, 'image/png')
  })

const loadImageFromUrl = (url) =>
  new Promise((resolve, reject) => {
    const image = new Image()
    image.onload = () => resolve(image)
    image.onerror = () => reject(new Error('图片解码失败'))
    image.src = url
  })

const createPreviewUrl = async (file, sourceUrl) => {
  try {
    const canvas = document.createElement('canvas')
    const context = canvas.getContext('2d')
    if (!context) {
      return sourceUrl
    }

    if (typeof createImageBitmap === 'function') {
      const bitmap = await createImageBitmap(file)
      try {
        const size = getPreviewSize(bitmap.width, bitmap.height)
        canvas.width = size.width
        canvas.height = size.height
        context.drawImage(bitmap, 0, 0, size.width, size.height)
      } finally {
        if (typeof bitmap.close === 'function') {
          bitmap.close()
        }
      }
      const blob = await canvasToBlob(canvas)
      return URL.createObjectURL(blob)
    }

    const image = await loadImageFromUrl(sourceUrl)
    const size = getPreviewSize(image.naturalWidth || image.width, image.naturalHeight || image.height)
    canvas.width = size.width
    canvas.height = size.height
    context.drawImage(image, 0, 0, size.width, size.height)
    const blob = await canvasToBlob(canvas)
    return URL.createObjectURL(blob)
  } catch (error) {
    return sourceUrl
  }
}

const CUT_MODES = ['local', 'doubao']

const clearSelectedResultState = () => {
  selectedResultByMode.local = -1
  selectedResultByMode.doubao = -1
}

const normalizeSelectionPayload = (payload) => {
  if (!payload || !payload.url) {
    return null
  }
  return {
    mode: payload.mode === 'local' ? 'local' : 'doubao',
    slotIndex: clampSlotIndex(payload.slotIndex),
    url: payload.url,
    manual: payload.manual === true
  }
}

const readSelectedPayload = () => normalizeSelectionPayload(workbench.selectedCutRef)

const setSelectedResultState = (payload) => {
  clearSelectedResultState()
  if (!payload) {
    return
  }
  selectedResultByMode[payload.mode] = payload.slotIndex
}

const getResultPayload = (mode, index) => {
  const normalizedMode = mode === 'local' ? 'local' : 'doubao'
  const slotIndex = clampSlotIndex(index)
  const slot = slotsByMode[normalizedMode][slotIndex]
  if (!slot?.result) {
    return null
  }
  return {
    mode: normalizedMode,
    slotIndex,
    url: slot.result
  }
}

const isSelectedPayloadValid = (payload) => {
  const normalizedPayload = normalizeSelectionPayload(payload)
  if (!normalizedPayload) {
    return false
  }
  const slot = slotsByMode[normalizedPayload.mode][normalizedPayload.slotIndex]
  return !!slot?.result && slot.result === normalizedPayload.url
}

const findLatestResultPayload = () => {
  let latest = null
  for (const mode of CUT_MODES) {
    slotsByMode[mode].forEach((slot, index) => {
      if (!slot?.result) {
        return
      }
      const updatedAt = Number(slot.resultUpdatedAt) || 0
      if (!latest || updatedAt > latest.updatedAt) {
        latest = { mode, slotIndex: index, url: slot.result, updatedAt }
        return
      }
      if (updatedAt === latest.updatedAt && mode === 'doubao' && latest.mode === 'local') {
        latest = { mode, slotIndex: index, url: slot.result, updatedAt }
      }
    })
  }

  if (!latest) {
    return null
  }

  return {
    mode: latest.mode,
    slotIndex: latest.slotIndex,
    url: latest.url
  }
}

const isSameSelectedPayload = (left, right) => {
  if (!left || !right) {
    return false
  }
  return (
    left.mode === right.mode &&
    left.slotIndex === right.slotIndex &&
    left.url === right.url &&
    left.manual === right.manual
  )
}

const setGlobalSelectedPayload = (payload, manual = false) => {
  const normalizedPayload = normalizeSelectionPayload(payload)
  if (!normalizedPayload) {
    clearSelectedResultState()
    if (workbench.selectedCutRef) {
      workbench.clearSelectedCutRef()
    }
    return
  }

  const targetPayload = {
    mode: normalizedPayload.mode,
    slotIndex: normalizedPayload.slotIndex,
    url: normalizedPayload.url,
    manual
  }
  setSelectedResultState(targetPayload)
  const currentPayload = readSelectedPayload()
  if (isSameSelectedPayload(currentPayload, targetPayload)) {
    return
  }
  workbench.setSelectedCutRef(targetPayload)
}

const reconcileSelectedResult = ({ promoteLatest = false } = {}) => {
  const selectedPayload = readSelectedPayload()
  if (selectedPayload && isSelectedPayloadValid(selectedPayload)) {
    setSelectedResultState(selectedPayload)
    if (!selectedPayload.manual && promoteLatest) {
      const latestPayload = findLatestResultPayload()
      if (!latestPayload) {
        return
      }
      if (
        latestPayload.mode !== selectedPayload.mode ||
        latestPayload.slotIndex !== selectedPayload.slotIndex ||
        latestPayload.url !== selectedPayload.url
      ) {
        setGlobalSelectedPayload(latestPayload, false)
      }
    }
    return
  }

  const fallbackPayload = findLatestResultPayload()
  if (fallbackPayload) {
    setGlobalSelectedPayload(fallbackPayload, false)
    return
  }
  setGlobalSelectedPayload(null, false)
}

const onSelectResultCard = (index) => {
  const payload = getResultPayload(currentMode.value, index)
  if (!payload) {
    return
  }
  setGlobalSelectedPayload(payload, true)
}

const resetSlot = (mode, index) => {
  const slot = slotsByMode[mode][index]
  revokeSlotUrls(slot)
  slot.file = null
  slot.sourceUrl = ''
  slot.preview = ''
  slot.compressedImage = ''
  slot.result = ''
  slot.resultUpdatedAt = 0
  slot.loading = false
  slot.error = ''
  slot.status = ''
  if (workbench.cutResultsByMode) {
    workbench.cutResultsByMode[mode][index] = ''
  }
  if (workbench.cutSourceImages) {
    workbench.cutSourceImages[mode][index] = ''
  }
}

const clearSingleImport = (index) => {
  const mode = currentMode.value
  const slot = slotsByMode[mode][index]
  revokeSlotUrls(slot)
  slot.file = null
  slot.sourceUrl = ''
  slot.preview = ''
  slot.compressedImage = ''
  if (workbench.cutSourceImages) {
    workbench.cutSourceImages[mode][index] = ''
  }
  syncStoreWithSlot(mode, index)
  workbench.saveNow?.()
}

const clearSingleResult = (index) => {
  const mode = currentMode.value
  const slot = slotsByMode[mode][index]
  slot.result = ''
  slot.resultUpdatedAt = 0
  if (workbench.cutResultsByMode) {
    workbench.cutResultsByMode[mode][index] = ''
  }
  reconcileSelectedResult()
  workbench.saveNow?.()
}

const clearAllSlots = () => {
  const mode = currentMode.value
  for (let i = 0; i < SLOT_COUNT; i++) {
    resetSlot(mode, i)
  }
  clearSelectedResultState()
  workbench.clearSelectedCutRef?.()
  syncStoreWithSlot(mode, activeSlotByMode[mode])
  workbench.saveNow?.()
}

const getStoreTargetByMode = (mode) => (mode === 'local' ? workbench.local : workbench.doubao)

const hydrateSlotFromStore = (mode) => {
  const normalizedMode = mode === 'local' ? 'local' : 'doubao'
  const target = getStoreTargetByMode(normalizedMode)
  const slotIndex = clampSlotIndex(activeSlotByMode[normalizedMode])
  const slot = slotsByMode[normalizedMode][slotIndex]
  if (!slot || !target) {
    return
  }

  const preview = typeof target.preview === 'string' ? target.preview : ''
  const fallbackImage = typeof target.image === 'string' ? target.image : ''
  const source = preview || (fallbackImage.startsWith('data:image') ? fallbackImage : '')
  if (source) {
    slot.sourceUrl = source
    slot.preview = source
  }

  slot.result = target.result || slot.result || ''
  slot.resultUpdatedAt = slot.result ? Date.now() : 0
  slot.loading = !!target.loading
  slot.error = target.error || ''
  slot.status = target.status || slot.status || (source ? '已导入' : '')
}

const syncStoreWithSlot = (mode, index) => {
  const normalizedIndex = clampSlotIndex(index)
  const slot = slotsByMode[mode][normalizedIndex]
  const target = getStoreTargetByMode(mode)

  if (!slot?.file) {
    if (!allowEmptySlotSync.value) {
      if (slot?.sourceUrl || slot?.preview || slot?.compressedImage) {
        target.preview = slot.sourceUrl || slot.preview || slot.compressedImage
      }
      if (slot?.compressedImage) {
        target.image = slot.compressedImage
      }
      if (slot?.result) {
        target.result = slot.result
      }
      if (slot?.status) {
        target.status = slot.status
      }
      if (slot?.error) {
        target.error = slot.error
      }
      if (slot?.loading) {
        target.loading = true
      }
      return
    }
    target.image = slot?.compressedImage || ''
    target.preview = slot?.sourceUrl || slot?.preview || slot?.compressedImage || ''
    target.result = slot?.result || ''
    target.loading = false
    target.error = slot?.error || ''
    target.status = slot?.status || ''
    target.raw = ''
    return
  }

  target.preview = slot.sourceUrl || slot.preview
  target.result = slot.result || ''
  target.image = slot.compressedImage || ''
  target.error = slot.error || ''
  target.status = slot.status || ''
  target.raw = slot.status || '已导入'
}

const setFileToSlot = (file, index, syncToStore = true) => {
  if (!isImageFile(file)) {
    return
  }

  const mode = currentMode.value
  const normalizedIndex = clampSlotIndex(index)
  const slot = slotsByMode[mode][normalizedIndex]
  if (!slot) {
    return
  }

  revokeSlotUrls(slot)
  const sourceUrl = URL.createObjectURL(file)
  slot.file = file
  slot.sourceUrl = sourceUrl
  slot.preview = ''
  slot.result = ''
  slot.resultUpdatedAt = 0
  slot.loading = false
  slot.error = ''
  slot.status = '已导入'

  if (workbench.cutResultsByMode) {
    workbench.cutResultsByMode[mode][normalizedIndex] = ''
  }

  if (workbench.cutSourceImages) {
    workbench.cutSourceImages[mode][normalizedIndex] = ''
  }

  reconcileSelectedResult()

  if (syncToStore) {
    activeSlotByMode[mode] = normalizedIndex
    syncStoreWithSlot(mode, normalizedIndex)
  }

  if (workbench.compressImage) {
    workbench.compressImage(file).then((compressed) => {
      if (slot.file !== file) return
      slot.compressedImage = compressed
      const target = getStoreTargetByMode(mode)
      target.image = compressed
      if (workbench.cutSourceImages) {
        workbench.cutSourceImages[mode][normalizedIndex] = compressed
      }
      workbench.saveNow?.()
    })
  }

  createPreviewUrl(file, sourceUrl).then((previewUrl) => {
    if (slot.file !== file || slot.sourceUrl !== sourceUrl) {
      if (previewUrl && previewUrl !== sourceUrl) {
        revokeObjectUrl(previewUrl)
      }
      return
    }
    slot.preview = previewUrl || sourceUrl
  })
}

const assignFilesFromIndex = (files, startIndex) => {
  const imageFiles = files.filter((file) => isImageFile(file))
  if (!imageFiles.length) {
    return
  }

  const mode = currentMode.value
  const baseIndex = clampSlotIndex(startIndex)
  let currentIndex = baseIndex
  for (const file of imageFiles) {
    if (currentIndex >= SLOT_COUNT) {
      break
    }
    setFileToSlot(file, currentIndex, false)
    currentIndex += 1
  }

  activeSlotByMode[mode] = baseIndex
  syncStoreWithSlot(mode, baseIndex)
}

const selectSlot = (index) => {
  const mode = currentMode.value
  const normalizedIndex = clampSlotIndex(index)
  activeSlotByMode[mode] = normalizedIndex
  syncStoreWithSlot(mode, normalizedIndex)
}

const releasePickerLock = () => {
  isPicking.value = false
}

const clearPendingSlotIndex = () => {
  pendingSlotIndex.value = -1
}

const resetPickerState = () => {
  releasePickerLock()
  clearPendingSlotIndex()
}

const consumePendingSlotIndex = (fallbackIndex) => {
  const pendingIndex = pendingSlotIndex.value
  clearPendingSlotIndex()
  if (Number.isInteger(pendingIndex) && pendingIndex >= 0) {
    return clampSlotIndex(pendingIndex)
  }
  return clampSlotIndex(fallbackIndex)
}

const armFocusRelease = () => {
  if (typeof window === 'undefined') {
    return
  }
  const onFocus = () => {
    window.removeEventListener('focus', onFocus)
    window.setTimeout(() => {
      if (isPicking.value) {
        releasePickerLock()
      }
    }, 0)
  }
  window.addEventListener('focus', onFocus, { once: true })
}

const pickSingleForSlot = (index) => {
  if (isPicking.value || !singleInput.value) {
    return
  }
  pendingSlotIndex.value = clampSlotIndex(index)
  isPicking.value = true
  clearInputValue(singleInput.value)
  armFocusRelease()
  singleInput.value.click()
}

const pickBatch = () => {
  if (isPicking.value || !batchInput.value) {
    return
  }
  pendingSlotIndex.value = -1
  isPicking.value = true
  clearInputValue(batchInput.value)
  armFocusRelease()
  batchInput.value.click()
}

const onSlotUploadRequest = (index, event) => {
  if (!event?.isTrusted) {
    return
  }
  selectSlot(index)
  pickSingleForSlot(index)
}

const onBatchUploadRequest = (event) => {
  if (!event?.isTrusted) {
    return
  }
  pickBatch()
}

const onImportCardClick = (index, event) => {
  selectSlot(index)
  const slot = slotsForCurrentMode.value[index]
  if (!slot?.preview && !slot?.sourceUrl && event?.isTrusted) {
    pickSingleForSlot(index)
  }
}

const onSingleFileChange = (event) => {
  const files = Array.from(event.target.files || [])
  const targetIndex = consumePendingSlotIndex(activeSlotIndex.value)
  releasePickerLock()
  try {
    if (files.length) {
      assignFilesFromIndex(files, targetIndex)
    }
  } catch (error) {
    const slot = slotsForCurrentMode.value[targetIndex]
    if (slot) {
      slot.error = error.message || '导入失败'
      slot.status = '导入失败'
    }
  }
  clearInputValue(event.target)
  resetPickerState()
}

const onBatchFileChange = (event) => {
  const files = Array.from(event.target.files || [])
  resetPickerState()
  try {
    if (files.length) {
      assignFilesFromIndex(files, 0)
    }
  } catch (error) {
    const slot = slotsForCurrentMode.value[0]
    if (slot) {
      slot.error = error.message || '导入失败'
      slot.status = '导入失败'
    }
  }
  clearInputValue(event.target)
}

const onPickerCancel = (event) => {
  resetPickerState()
  clearInputValue(event.target)
}

const onImportDragEnter = (index) => {
  currentDragFlags.value[index] = true
}

const onImportDragLeave = (index) => {
  currentDragFlags.value[index] = false
}

const onImportDrop = (event, index) => {
  currentDragFlags.value[index] = false
  const files = Array.from(event.dataTransfer?.files || [])
  if (!files.length) {
    return
  }
  try {
    assignFilesFromIndex(files, index)
  } catch (error) {
    const slot = slotsForCurrentMode.value[index]
    if (slot) {
      slot.error = error.message || '导入失败'
      slot.status = '导入失败'
    }
  }
}

const downloadImage = (url, fileName) => {
  if (!url) {
    return
  }
  const anchor = document.createElement('a')
  anchor.href = url
  anchor.download = fileName
  anchor.click()
}

hydrateSlotFromStore('local')
hydrateSlotFromStore('doubao')

let hasRestoredFromPersist = false

const restoreSlotsFromPersistedState = () => {
  if (hasRestoredFromPersist) return
  const hasData = workbench.cutSourceImages?.local?.some(Boolean) ||
    workbench.cutSourceImages?.doubao?.some(Boolean) ||
    workbench.cutResultsByMode?.local?.some(Boolean) ||
    workbench.cutResultsByMode?.doubao?.some(Boolean)
  if (!hasData) return

  hasRestoredFromPersist = true
  for (const mode of ['local', 'doubao']) {
    for (let i = 0; i < SLOT_COUNT; i++) {
      const storedResult = workbench.cutResultsByMode?.[mode]?.[i]
      if (storedResult && !slotsByMode[mode][i].result) {
        slotsByMode[mode][i].result = storedResult
        slotsByMode[mode][i].resultUpdatedAt = Date.now()
      }
      const storedSource = workbench.cutSourceImages?.[mode]?.[i]
      if (storedSource && !slotsByMode[mode][i].compressedImage) {
        slotsByMode[mode][i].compressedImage = storedSource
        if (!slotsByMode[mode][i].sourceUrl && !slotsByMode[mode][i].preview) {
          slotsByMode[mode][i].sourceUrl = storedSource
          slotsByMode[mode][i].preview = storedSource
        }
        slotsByMode[mode][i].status = slotsByMode[mode][i].status || '已恢复'
      }
    }
  }
  reconcileSelectedResult()
}

// Watch for store restoreState to populate cutSourceImages/cutResultsByMode
watch(
  () => [JSON.stringify(workbench.cutSourceImages || {}), JSON.stringify(workbench.cutResultsByMode || {})],
  () => { restoreSlotsFromPersistedState() },
  { immediate: true }
)

let isBatchRunning = false

const processOneSlot = async (mode, idx) => {
  const slot = slotsByMode[mode][idx]
  slot.loading = true
  slot.error = ''
  slot.status = '处理中...'

  try {
    const result = await workbench.removeBgDirect(slot.compressedImage, mode)
    slot.result = result || ''
    slot.resultUpdatedAt = slot.result ? Date.now() : 0
    slot.status = result ? '完成' : ''
    if (workbench.cutResultsByMode) {
      workbench.cutResultsByMode[mode][idx] = slot.result || ''
    }
    workbench.saveNow?.()
  } catch (e) {
    slot.error = e.message || '处理失败'
    slot.status = ''
  } finally {
    slot.loading = false
  }
}

const batchCutAll = async () => {
  const mode = currentMode.value
  const slots = slotsByMode[mode]
  const pending = []
  for (let i = 0; i < SLOT_COUNT; i++) {
    if (slots[i].compressedImage && !slots[i].result) {
      pending.push(i)
    }
  }
  if (pending.length === 0) {
    return
  }

  isBatchRunning = true
  workbench.batchCutRunning = true
  startRemoveProgress()

  await Promise.all(pending.map((idx) => processOneSlot(mode, idx)))

  isBatchRunning = false
  workbench.batchCutRunning = false
  stopRemoveProgress(100)
  reconcileSelectedResult({ promoteLatest: true })
}

workbench.batchCutHandler = batchCutAll

watch(
  () => workbench.activeLoading,
  (loading) => {
    if (isBatchRunning) return

    if (loading) {
      wasActiveLoading.value = true
      startRemoveProgress()
      return
    }

    if (!wasActiveLoading.value) {
      return
    }

    wasActiveLoading.value = false
    stopRemoveProgress(workbench.activeError ? 0 : 100)
  }
)

watch(
  () => workbench.cutTab,
  (mode) => {
    syncStoreWithSlot(mode, activeSlotByMode[mode])
  },
  { immediate: true }
)

watch(
  () => [workbench.cutTab, workbench.activeLoading, workbench.activeError, workbench.activeStatus],
  ([mode, loading, error, status]) => {
    const slot = slotsByMode[mode][activeSlotByMode[mode]]
    if (!slot) {
      return
    }
    slot.loading = loading
    slot.error = error || ''
    slot.status = status || slot.status || ''
  },
  { immediate: true }
)

watch(
  () => workbench.activeResult,
  (value) => {
    const mode = workbench.cutTab
    const slotIndex = activeSlotByMode[mode]
    const slot = slotsByMode[mode][slotIndex]
    if (!slot) {
      return
    }

    slot.result = value || ''
    slot.resultUpdatedAt = slot.result ? Date.now() : 0
    if (workbench.cutResultsByMode) {
      workbench.cutResultsByMode[mode][slotIndex] = slot.result || ''
    }
    if (slot.result) {
      workbench.saveNow?.()
    }
    reconcileSelectedResult({ promoteLatest: !!slot.result })
  },
  { immediate: true }
)

watch(
  () => workbench.doubao.result,
  (value) => {
    if (!value) return
    const idx = activeSlotByMode.doubao
    const slot = slotsByMode.doubao[idx]
    if (slot && !slot.result) {
      slot.result = value
      slot.resultUpdatedAt = Date.now()
      if (workbench.cutResultsByMode) {
        workbench.cutResultsByMode.doubao[idx] = value
      }
      workbench.saveNow?.()
    }
  }
)

watch(
  () => workbench.local.result,
  (value) => {
    if (!value) return
    const idx = activeSlotByMode.local
    const slot = slotsByMode.local[idx]
    if (slot && !slot.result) {
      slot.result = value
      slot.resultUpdatedAt = Date.now()
      if (workbench.cutResultsByMode) {
        workbench.cutResultsByMode.local[idx] = value
      }
      workbench.saveNow?.()
    }
  }
)

watch(
  () => [workbench.doubao.image, workbench.local.image],
  ([doubaoImage, localImage]) => {
    if (doubaoImage) {
      const slot = slotsByMode.doubao[activeSlotByMode.doubao]
      if (slot && !slot.sourceUrl && !slot.preview) {
        slot.sourceUrl = doubaoImage
        slot.preview = doubaoImage
      }
    }
    if (localImage) {
      const slot = slotsByMode.local[activeSlotByMode.local]
      if (slot && !slot.sourceUrl && !slot.preview) {
        slot.sourceUrl = localImage
        slot.preview = localImage
      }
    }
  }
)

watch(
  () => workbench.selectedCutRef,
  () => {
    reconcileSelectedResult()
  },
  { deep: true }
)

nextTick(() => {
  allowEmptySlotSync.value = true
})

onBeforeUnmount(() => {
  stopRemoveProgress(0)
  workbench.batchCutRunning = false
  workbench.batchCutHandler = null
})
</script>
